import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useBrand } from '../../contexts/BrandContext';
import { 
  BarChart3, 
  Shield, 
  Smartphone, 
  Clock, 
  TrendingUp, 
  Users,
  Award,
  Globe
} from 'lucide-react';

const FeaturesSection = ({ language, onRegister }) => {
  const { tt } = useLanguage();
  const { brandName } = useBrand();
  
  const translations = {
    ru: {
      title: `Почему трейдеры выбирают ${brandName}`,
      subtitle: 'Современная торговая платформа с полным набором инструментов для успешной торговли',
      features: [
        {
          icon: BarChart3,
          title: 'Профессиональные графики',
          description: 'Интеграция с TradingView. Более 100 технических индикаторов и инструментов анализа'
        },
        {
          icon: Shield,
          title: 'Безопасность и надежность',
          description: 'Ваши средства защищены современными методами шифрования и находятся в безопасности'
        },
        {
          icon: Smartphone,
          title: 'Торговля везде',
          description: 'Адаптивная веб-платформа работает на всех устройствах - компьютер, планшет, смартфон'
        },
        {
          icon: Clock,
          title: 'Торговля 24/7',
          description: 'Рынки криптовалют и форекс доступны круглосуточно. Торгуйте в любое удобное время'
        },
        {
          icon: TrendingUp,
          title: 'Высокие выплаты',
          description: 'Получайте до 95% прибыли с успешных сделок. Одни из лучших условий на рынке'
        },
        {
          icon: Users,
          title: 'Поддержка 24/7',
          description: 'Круглосуточная техническая поддержка на русском языке. Всегда готовы помочь'
        },
        {
          icon: Award,
          title: 'Обучение трейдингу',
          description: 'Бесплатные обучающие материалы, вебинары и персональные консультации'
        },
        {
          icon: Globe,
          title: 'Международная компания',
          description: 'Работаем по всему миру. Миллионы довольных клиентов в 150+ странах'
        }
      ]
    },
    en: {
      title: `Why traders choose ${brandName}`,
      subtitle: 'Modern trading platform with complete set of tools for successful trading',
      features: [
        {
          icon: BarChart3,
          title: 'Professional charts',
          description: 'TradingView integration. More than 100 technical indicators and analysis tools'
        },
        {
          icon: Shield,
          title: 'Security and reliability',
          description: 'Your funds are protected by modern encryption methods and are completely safe'
        },
        {
          icon: Smartphone,
          title: 'Trade anywhere',
          description: 'Responsive web platform works on all devices - computer, tablet, smartphone'
        },
        {
          icon: Clock,
          title: '24/7 trading',
          description: 'Cryptocurrency and forex markets are available around the clock. Trade anytime'
        },
        {
          icon: TrendingUp,
          title: 'High payouts',
          description: 'Get up to 95% profit from successful trades. Some of the best conditions on the market'
        },
        {
          icon: Users,
          title: '24/7 support',
          description: 'Round-the-clock technical support in Russian. Always ready to help'
        },
        {
          icon: Award,
          title: 'Trading education',
          description: 'Free educational materials, webinars and personal consultations'
        },
        {
          icon: Globe,
          title: 'International company',
          description: 'We work worldwide. Millions of satisfied customers in 150+ countries'
        }
      ]
    },
    kk: {
      title: `Неліктен трейдерлер ${brandName} таңдайды`,
      subtitle: 'Табысты сауда үшін құралдардың толық жиынтығымен заманауи сауда платформасы',
      features: [
        {
          icon: BarChart3,
          title: 'Кәсіби графиктер',
          description: 'TradingView интеграциясы. 100-ден астам техникалық индикаторлар мен талдау құралдары'
        },
        {
          icon: Shield,
          title: 'Қауіпсіздік және сенімділік',
          description: 'Қаражатыңыз заманауи шифрлау әдістерімен қорғалған және толығымен қауіпсіз'
        },
        {
          icon: Smartphone,
          title: 'Кез келген жерде сауда',
          description: 'Адаптивті веб-платформа барлық құрылғыларда жұмыс істейді - компьютер, планшет, смартфон'
        },
        {
          icon: Clock,
          title: '24/7 сауда',
          description: 'Криптовалюта және форекс нарықтары тәулік бойы қолжетімді. Кез келген уақытта сауда жасаңыз'
        },
        {
          icon: TrendingUp,
          title: 'Жоғары төлемдер',
          description: 'Табысты мәмілелерден 95%-ға дейін пайда алыңыз. Нарықтағы ең жақсы жағдайлардың бірі'
        },
        {
          icon: Users,
          title: '24/7 қолдау',
          description: 'Орыс тілінде тәулік бойы техникалық қолдау. Әрқашан көмектесуге дайынбыз'
        },
        {
          icon: Award,
          title: 'Трейдингті оқыту',
          description: 'Тегін оқу материалдары, вебинарлар және жеке кеңестер'
        },
        {
          icon: Globe,
          title: 'Халықаралық компания',
          description: 'Біз бүкіл әлемде жұмыс істейміз. 150+ елдегі миллиондаған риза клиенттер'
        }
      ]
    }
  };

  const t = translations[language] || translations['ru'];

  return (
    <div className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.features.map((feature, index) => (
            <div
              key={index}
              className="group p-6 rounded-2xl bg-gray-50 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-green-50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-green-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <feature.icon className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {feature.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-2xl p-8 md:p-12">
            <h3 className="text-3xl font-bold text-white mb-4">
              {language === 'ru' ? 'Готовы начать торговать?' : 'Ready to start trading?'}
            </h3>
            <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
              {language === 'ru' 
                ? 'Присоединяйтесь к миллионам успешных трейдеров. Начните торговать уже сегодня.'
                : 'Join millions of successful traders. Start trading today.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={onRegister}
                className="px-8 py-3 bg-white text-emerald-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                {language === 'ru' ? 'Создать счет' : 'Create Account'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturesSection;